import type { DimensionStore } from '../dimensions';
import { anchorToItem, anchorToPoint } from '../dimensions';
import type { Metrics, Pt } from '../metrics';
import type { Anchor } from '../types';

export type ToolMode = 'select' | 'place_item' | 'dimension' | 'calibrate';
export type DimensionPlacementMode = 'free' | 'orthogonal';

const DEFAULT_DIMENSION_OFFSET_MM = 200;
const FALLBACK_DIMENSION_OFFSET_SOURCE_PX = 24;

export interface SnapTarget {
  id: string;
  point: Pt;
}

interface DraftPoint {
  point: Pt;
  anchor: Anchor;
}

export interface DimensionPreview {
  a: Pt;
  b: Pt;
  mode: 'calibration' | 'dimension';
}

export interface DimensionTool {
  mode: ToolMode;
  requestDimensionMode(): void;
  onPointerDown(p: Pt, snapThresholdSourcePx?: number): void;
  onPointerMove(p: Pt, snapThresholdSourcePx?: number): void;
  onPointerUp(p: Pt, snapThresholdSourcePx?: number): void;
  getPreview(): DimensionPreview | null;
  cancel(): void;
}

interface DimensionToolDeps {
  metrics: Metrics;
  store: DimensionStore;
  getSnapTargets(): SnapTarget[];
  getPlacementMode(): DimensionPlacementMode;
  getBackgroundHash(): string | null;
  onCalibrationSegment?: (a: Pt, b: Pt) => void;
  onDimensionsChange?: () => void;
}

function nearestSnapPoint(p: Pt, targets: SnapTarget[], thresholdSourcePx: number): DraftPoint {
  let best: { target: SnapTarget; distance: number } | null = null;
  for (const target of targets) {
    const distance = Math.hypot(target.point.x - p.x, target.point.y - p.y);
    if (!best || distance < best.distance) best = { target, distance };
  }
  return best && best.distance <= thresholdSourcePx
    ? { point: best.target.point, anchor: anchorToItem(best.target.id) }
    : { point: p, anchor: anchorToPoint(p) };
}

function orthogonalEnd(start: DraftPoint, end: DraftPoint): DraftPoint {
  const dx = end.point.x - start.point.x;
  const dy = end.point.y - start.point.y;
  const point = Math.abs(dx) >= Math.abs(dy)
    ? { x: end.point.x, y: start.point.y }
    : { x: start.point.x, y: end.point.y };
  return { point, anchor: anchorToPoint(point) };
}

function dimensionEndPoint(start: DraftPoint, end: DraftPoint, placementMode: DimensionPlacementMode): DraftPoint {
  return placementMode === 'orthogonal' ? orthogonalEnd(start, end) : end;
}

function defaultOffsetSourcePx(metrics: Metrics): number {
  const calibration = metrics.getCalibration();
  return calibration
    ? calibration.pxPerMm * DEFAULT_DIMENSION_OFFSET_MM
    : FALLBACK_DIMENSION_OFFSET_SOURCE_PX;
}

export function createDimensionTool(deps: DimensionToolDeps): DimensionTool {
  let mode: ToolMode = 'select';
  let draftStart: DraftPoint | null = null;
  let preview: DimensionPreview | null = null;

  const hasValidCalibration = () => {
    const backgroundHash = deps.getBackgroundHash();
    return Boolean(backgroundHash && deps.metrics.getCalibration() && !deps.metrics.isStale(backgroundHash));
  };

  return {
    get mode() {
      return mode;
    },
    requestDimensionMode: () => {
      mode = hasValidCalibration() ? 'dimension' : 'calibrate';
      draftStart = null;
      preview = null;
    },
    onPointerDown: (p, snapThresholdSourcePx = 18) => {
      if (mode !== 'calibrate' && mode !== 'dimension') return;
      draftStart = nearestSnapPoint(p, deps.getSnapTargets(), snapThresholdSourcePx);
      preview = null;
    },
    onPointerMove: (p, snapThresholdSourcePx = 18) => {
      if (!draftStart || (mode !== 'calibrate' && mode !== 'dimension')) return;
      const snappedEnd = nearestSnapPoint(p, deps.getSnapTargets(), snapThresholdSourcePx);
      const end = mode === 'dimension'
        ? dimensionEndPoint(draftStart, snappedEnd, deps.getPlacementMode())
        : snappedEnd;
      preview = {
        a: draftStart.point,
        b: end.point,
        mode: mode === 'calibrate' ? 'calibration' : 'dimension',
      };
    },
    onPointerUp: (p, snapThresholdSourcePx = 18) => {
      if (!draftStart || (mode !== 'calibrate' && mode !== 'dimension')) return;
      const snappedEnd = nearestSnapPoint(p, deps.getSnapTargets(), snapThresholdSourcePx);
      const end = mode === 'dimension'
        ? dimensionEndPoint(draftStart, snappedEnd, deps.getPlacementMode())
        : snappedEnd;
      if (Math.hypot(end.point.x - draftStart.point.x, end.point.y - draftStart.point.y) < 1) {
        draftStart = null;
        preview = null;
        return;
      }
      if (mode === 'calibrate') {
        deps.onCalibrationSegment?.(draftStart.point, end.point);
      } else {
        deps.store.add(draftStart.anchor, end.anchor, defaultOffsetSourcePx(deps.metrics));
        deps.onDimensionsChange?.();
      }
      draftStart = null;
      preview = null;
    },
    getPreview: () => preview,
    cancel: () => {
      mode = 'select';
      draftStart = null;
      preview = null;
    },
  };
}
